'use client'

import { motion } from 'motion/react'
import { ReactNode, useId, useMemo } from 'react'
import { cn } from '@/utils/styles/cn'

export type IBannerItem = ReactNode

interface IBannerProps {
    // We need all items initially to duplicate them, but we also need to pass the index dynamically, as we would otherwise have duplicate keys
    // Therefore, we pass a function that returns the item, and we pass the index to it
    items: IBannerItem[]
    minItems?: number
    className?: string
    innerContainerClassName?: string
    delay?: number
}

export const Banner = ({
    items: rawItems,
    minItems = 200,
    className,
    innerContainerClassName,
    delay = 0,
}: IBannerProps) => {
    const componentId = useId()

    const items = useMemo(() => {
        // To make the banner look more full, we duplicate the items until we reach the minItems requirement

        if (!rawItems?.length) return []

        if (rawItems.length > minItems) {
            return rawItems
        }

        const next = [...rawItems]

        while (next.length < minItems) {
            next.push(...rawItems)
        }

        return next
    }, [rawItems, minItems])

    // The duration of the passthrough animation must always come down to the same speed, so it must be reliant on the items length
    const passthroughDuration = items.length / 0.4

    if (!items.length) return null

    return (
        <div className={cn('max-w-[100vw] min-w-[100vw] overflow-x-hidden', className)}>
            <motion.div
                initial={{ x: 0 }}
                animate={{ x: -items.length * 100 }}
                transition={{
                    delay,
                    duration: passthroughDuration,
                    ease: 'linear',
                    repeat: Infinity,
                }}
                className={cn(
                    'flex flex-row items-center py-10 min-w-[100vw] gap-[70px] md:gap-x-[102px] px-[100px]',
                    innerContainerClassName
                )}
            >
                {items.map((item, i) => {
                    return (
                        <div className="min-w-fit" key={'banner' + i + componentId}>
                            {item}
                        </div>
                    )
                })}
            </motion.div>
        </div>
    )
}
